/**
 * Session Manager for TalkFlow Chat
 * Handles session creation, reuse, and cleanup
 */

import { sendMessage, getChatSessions, createSession } from './api.js';

class SessionManager {
    constructor() {
        this.currentSession = null;
        this.sessionCache = new Map(); // tenantId -> sessionId
        this.sessionTimeout = 30 * 60 * 1000; // 30 minutes
        this.lastActivity = new Map(); // sessionId -> timestamp
        this.sessionCreationAttempts = new Map(); // tenantId -> { attempts, lastAttempt }
        this.maxCreationAttempts = 3;
        this.creationCooldown = 10 * 1000; // 10 seconds
    }

    /**
     * Check if we can create a new session (rate limiting)
     */
    canCreateSession(tenantId) {
        const cacheKey = tenantId || 'default';
        const attemptInfo = this.sessionCreationAttempts.get(cacheKey);

        if (!attemptInfo) {
            return true;
        }

        const now = Date.now();
        const timeSinceLastAttempt = now - attemptInfo.lastAttempt;

        // Reset attempts if cooldown period has passed
        if (timeSinceLastAttempt > this.creationCooldown) {
            this.sessionCreationAttempts.delete(cacheKey);
            return true;
        }

        // Check if we've exceeded max attempts
        return attemptInfo.attempts < this.maxCreationAttempts;
    }

    /**
     * Record a session creation attempt
     */
    recordCreationAttempt(tenantId) {
        const cacheKey = tenantId || 'default';
        const now = Date.now();
        const attemptInfo = this.sessionCreationAttempts.get(cacheKey) || { attempts: 0, lastAttempt: 0 };

        attemptInfo.attempts++;
        attemptInfo.lastAttempt = now;

        this.sessionCreationAttempts.set(cacheKey, attemptInfo);
    }

    /**
     * Get or create a session for a tenant
     */
    async getSession(tenantId) {
        const cacheKey = tenantId || 'default';

        // Check if we have a cached session
        if (this.sessionCache.has(cacheKey)) {
            const sessionId = this.sessionCache.get(cacheKey);

            // Check if session is still active (not expired)
            const lastActivity = this.lastActivity.get(sessionId);
            if (lastActivity && (Date.now() - lastActivity) < this.sessionTimeout) {
                return sessionId;
            } else {
                this.sessionCache.delete(cacheKey);
                this.lastActivity.delete(sessionId);
            }
        }

        // Check rate limiting before creating new session
        if (!this.canCreateSession(tenantId)) {
            const attemptInfo = this.sessionCreationAttempts.get(cacheKey);
            const waitTime = Math.ceil((this.creationCooldown - (Date.now() - attemptInfo.lastAttempt)) / 1000);
            throw new Error(`Too many session creation attempts. Please wait ${waitTime} seconds before trying again.`);
        }

        // Record the attempt
        this.recordCreationAttempt(tenantId);

        // Create new session by calling createSession
        try {
            const newSessionId = await createSession(tenantId);

            if (!newSessionId) {
                throw new Error('No session ID returned from createSession');
            }

            // Cache the session
            this.sessionCache.set(cacheKey, newSessionId);
            this.lastActivity.set(newSessionId, Date.now());
            this.currentSession = newSessionId;

            // Reset creation attempts on success
            this.sessionCreationAttempts.delete(cacheKey);

            return newSessionId;

        } catch (error) {
            // Don't retry immediately if it's a rate limit error
            if (error.message.includes('429') || error.message.includes('Too Many Requests')) {
                throw new Error('Rate limited. Please wait before creating a new session.');
            }

            throw error;
        }
    }

    /**
     * Send message using existing session
     */
    async sendMessageWithSession(message, tenantId, history = []) {
        try {
            // Get or create session
            const sessionId = await this.getSession(tenantId);

            // Update last activity
            this.lastActivity.set(sessionId, Date.now());

            // Send message with existing session
            const response = await sendMessage(
                message,
                tenantId,
                history,
                null, // use default API URL
                sessionId // use existing session
            );

            return response;

        } catch (error) {
            // Handle rate limiting
            if (error.message.includes('429') || error.message.includes('Rate limited')) {
                throw error; // Don't retry rate limited requests
            }

            // If session error, try creating new session (but only once)
            if (error.message.includes('404') || error.message.includes('session')) {
                this.sessionCache.delete(tenantId || 'default');

                // Only retry once to prevent infinite loops
                if (!error.isRetry) {
                    const retryError = new Error(error.message);
                    retryError.isRetry = true;

                    try {
                        return await this.sendMessageWithSession(message, tenantId, history);
                    } catch (retryErr) {
                        retryErr.isRetry = true;
                        throw retryErr;
                    }
                }
            }

            throw error;
        }
    }

    /**
     * Clear session cache for a tenant
     */
    clearSession(tenantId) {
        const cacheKey = tenantId || 'default';
        const sessionId = this.sessionCache.get(cacheKey);
        if (sessionId) {
            this.sessionCache.delete(cacheKey);
            this.lastActivity.delete(sessionId);
        }
    }

    /**
     * Clear all sessions
     */
    clearAllSessions() {
        this.sessionCache.clear();
        this.lastActivity.clear();
        this.currentSession = null;
    }

    /**
     * Get current session info
     */
    getSessionInfo(tenantId) {
        const cacheKey = tenantId || 'default';
        const sessionId = this.sessionCache.get(cacheKey);

        if (sessionId) {
            const lastActivity = this.lastActivity.get(sessionId);
            const timeLeft = this.sessionTimeout - (Date.now() - lastActivity);

            return {
                sessionId,
                lastActivity: new Date(lastActivity),
                timeLeft: Math.max(0, timeLeft),
                isExpired: timeLeft <= 0
            };
        }

        return null;
    }

    /**
     * Cleanup expired sessions
     */
    cleanupExpiredSessions() {
        const now = Date.now();
        const expiredSessions = [];
        for (const [sessionId, lastActivity] of this.lastActivity.entries()) {
            if ((now - lastActivity) > this.sessionTimeout) {
                expiredSessions.push(sessionId);
            }
        }
        for (const sessionId of expiredSessions) {
            this.lastActivity.delete(sessionId);
            for (const [tenantId, cachedSessionId] of this.sessionCache.entries()) {
                if (cachedSessionId === sessionId) {
                    this.sessionCache.delete(tenantId);
                    break;
                }
            }
        }
    }

    /**
     * Start automatic cleanup
     */
    startCleanup() {
        // Cleanup every 5 minutes
        setInterval(() => {
            this.cleanupExpiredSessions();
        }, 5 * 60 * 1000);
    }
}

// Create global session manager instance
const sessionManager = new SessionManager();

// Start automatic cleanup
sessionManager.startCleanup();

export default sessionManager;

// Export convenience functions
export const sendMessageWithSession = (message, tenantId, history) =>
    sessionManager.sendMessageWithSession(message, tenantId, history);

export const clearSession = (tenantId) =>
    sessionManager.clearSession(tenantId);

export const getSessionInfo = (tenantId) =>
    sessionManager.getSessionInfo(tenantId); 